<template>
    <v-container pa-0>

        <v-fade-transition mode="out-in">

            <!-- change retailer intro -->
            <v-card v-if="step === 0"
                    key="step1"
                    class="px-2 pt-2 pb-6 mt-3" flat tile>
                <div class="d-flex flex-row align-center mb-2">
                    <back-button :fallback-route="fallbackBackButtonRoute"></back-button>
                </div>
                <v-card-title class="headline font-weight-bold pa-2">
                    {{ $vuetify.lang.t('$vuetify.change_retailer.headline') }}
                </v-card-title>
                <div class="px-2 mb-5 body-1" v-html="$vuetify.lang.t( '$vuetify.change_retailer.intro_text' )"></div>
                <div class="px-2 mb-5 font-weight-bold">
                    {{ $vuetify.lang.t( '$vuetify.change_retailer.before_button' ) }}
                </div>
                <div class="d-flex justify-center">
                    <v-btn color="primary darken-1" @click.prevent="nextStep">
                        {{ $vuetify.lang.t('$vuetify.buttons.change_retailer_start') }}
                    </v-btn>
                </div>
            </v-card>
            <!-- /change retailer intro -->

            <!-- change retailer step 1 -->
            <v-card v-else-if="step === 1"
                    key="step2"
                    class="px-2 pt-2 pb-6 mt-3" flat tile>
                <div class="d-flex flex-row align-center mb-2">
                    <v-btn icon @click.prevent="previousStep">
                        <v-icon color="primary">mdi-arrow-left-circle-outline</v-icon>
                    </v-btn>
                </div>
                <error-list :errors="errors"></error-list>

                <template>
                    <v-card-title class="headline font-weight-bold pa-2">
                        {{ $vuetify.lang.t('$vuetify.change_retailer.form.step_1_headline') }}
                    </v-card-title>
                    <div class="px-2 mb-5 body-1">
                        {{ $vuetify.lang.t('$vuetify.change_retailer.form.step_1_description') }}
                    </div>
                    <build-form
                            :loader-key="loader"
                            :form-items="items"
                            :actions="actions"
                            :rules="rules"
                            :form.sync="form"
                            ref="form"
                            class="px-2"
                    />
                </template>
            </v-card>
            <!-- /change retailer step 1 -->

            <!-- change retailer step 2 - preview data -->
            <v-card v-else-if="step === 2"
                    class="px-2 pt-2 pb-6 mt-3" flat tile>
                <div class="d-flex flex-row align-center mb-2">
                    <v-btn icon @click.prevent="previousStep">
                        <v-icon color="primary">mdi-arrow-left-circle-outline</v-icon>
                    </v-btn>
                </div>
                <v-card-title class="headline font-weight-bold pa-2">
                    {{ $vuetify.lang.t('$vuetify.change_retailer.form.preview_headline') }}
                </v-card-title>
                <div class="px-2 body-1">
                    {{ $vuetify.lang.t('$vuetify.change_retailer.form.preview_description') }}
                </div>

                <v-row class="mx-2 my-2 mt-4">
                    <v-col cols="6" md="3" class="secondary--text py-0 pl-0 pr-4">
                        {{ $vuetify.lang.t('$vuetify.change_retailer.new_retailer_number') }}
                    </v-col>
                    <v-col cols="6" md="9" class="pa-0">
                        {{ form.new_retailer_number }}<br />
                        <template v-if="company && company.name && company.address_city">
                            {{ company.name }} {{ company.address_city }}
                        </template>
                    </v-col>
                </v-row>
                <v-row class="mx-2 my-2">
                    <v-col cols="6" md="3" class="secondary--text py-0 pl-0 pr-4">
                        {{ $vuetify.lang.t('$vuetify.change_retailer.new_contact_data') }}
                    </v-col>
                    <v-col cols="6" md="9" class="pa-0">
                        {{ form.business_email }}<br/>
                        {{ form.business_phone }}
                    </v-col>
                </v-row>
                <v-row class="mx-2 my-2 mb-6">
                    <v-col cols="6" md="3" class="secondary--text py-0 pl-0 pr-4">
                        {{ $vuetify.lang.t('$vuetify.form_items.employment_type') }}
                    </v-col>
                    <v-col cols="6" md="9" class="pa-0">
                        {{ $vuetify.lang.t(`$vuetify.employment_type.${form.employment_type}`) }}
                    </v-col>
                </v-row>

                <div class="px-2 mb-5 body-1">
                    <div class="mb-2">
                        {{ $vuetify.lang.t('$vuetify.change_retailer.form.preview_instruction') }}
                    </div>
                    <h4 class="mb-2">{{ $vuetify.lang.t('$vuetify.change_retailer.form.preview_headline_attention') }}</h4>
                    <div class="subtitle-1" v-html="$vuetify.lang.t('$vuetify.change_retailer.form.preview_notes')"></div>
                </div>

                <!-- submit-button -->
                <div class="d-flex justify-center">
                    <v-btn color="primary darken-1" @click="submit" :loading="$wait.waiting(loader)" :disabled="$wait.waiting(loader)">
                        {{ $vuetify.lang.t(`$vuetify.buttons.change_retailer_checkdata`) }}
                    </v-btn>
                </div>
            </v-card>
            <!-- /change retailer step 2 - preview data -->

            <!-- change retailer step 3 - confirmation -->
            <v-card v-else-if="step === 3"
                    class="px-2 pt-2 pb-6 mt-3" flat tile>
                <div class="d-flex flex-row align-center mb-2">
                    <back-button :fallback-route="fallbackBackButtonRoute"></back-button>
                </div>
                <v-card-title class="headline font-weight-bold pa-2">
                    {{ $vuetify.lang.t('$vuetify.change_retailer.completed_headline') }}
                </v-card-title>
                <div class="px-2 mb-5 body-1" v-html="$vuetify.lang.t('$vuetify.change_retailer.completed_description')"></div>

                <v-alert type="success"
                         class="mx-2"
                         icon="mdi-logout-variant"
                         text
                         border="left">
                    {{ $vuetify.lang.t('$vuetify.change_retailer.completed_loggedout') }}
                </v-alert>
            </v-card>
            <!-- /change retailer step 3 - confirmation -->

        </v-fade-transition>

    </v-container>
</template>
<script>
    // 3rd
    import { mapState } from 'vuex';
    // components
    import BackButton from '../../components/BackButton';
    import BuildForm from '../../components/Builders/BuildForm';
    import ErrorList from '../../components/ErrorList';
    // utils
    import {
        logOutUser,
        setPageTitle,
        transformArrayToAutoSelectValues,
        transformArrayToSelectValues
    } from '../../utils/helper';
    import {
        LOADER_NEW_RETAILER_SUBMISSION,
        ROUTE_CHANGE_RETAILER_SUCCESS,
        ROUTE_PROFILE
    } from '../../utils/constants';
    import {
        new_retailer_number,
        business_email,
        business_phone,
        employment_type,
    } from '../../utils/formRules';
    // models
    import Profile from '../../models/Profile';
    import { errorHandler } from "../../utils/api";

    export default {
        name: 'change-retailer',
        components: {
            BackButton,
            BuildForm,
            ErrorList,
        },
        data() {
            return {
                company: {},
                step: 0,
                maxSteps: 2,
                errors: {},
                form: {},
                rules: {
                    new_retailer_number,
                    business_email,
                    business_phone,
                    employment_type,
                },
                newRetailerName: '',
                showForm: true,
            };
        },
        computed: {
            ...mapState(['employmentTypes', 'companies']),
            fallbackBackButtonRoute() {
                return { name: ROUTE_PROFILE };
            },
            transformedCompanies() {
                return transformArrayToAutoSelectValues(this.companies);
            },
            employment_types() {
                return transformArrayToSelectValues(
                    this.employmentTypes,
                    '$vuetify.employment_type.'
                );
            },
            loader() {
                return LOADER_NEW_RETAILER_SUBMISSION;
            },
            items() {
                return [
                    {
                        is: 'text',
                        name: 'new_retailer_number',
                        label: '$vuetify.form_items.new_retailer_number',
                    },
                    {
                        is: 'text',
                        name: 'business_email',
                        hint: '$vuetify.form_item_hints.business_email',
                        label: '$vuetify.form_items.business_email_new',
                        optionalBy: 'employment_type:freelancer',
                    },
                    {
                        is: 'phone',
                        name: 'business_phone',
                        label: '$vuetify.form_items.business_phone_new',
                        optionalBy: 'employment_type:freelancer',
                    },
                    {
                        is: 'select',
                        name: 'employment_type',
                        options: this.employment_types,
                    },
                ];
            },
            actions() {
                return [
                    {
                        is: 'button',
                        name: 'change_retailer_form_submit',
                        color: 'primary darken-1',
                        clickMethod: this.validate,
                    },
                ];
            },
        },
        methods: {
            async validate() {
                this.$wait.start(LOADER_NEW_RETAILER_SUBMISSION);

                let result = null;
                try {
                    result = await Profile.validateNewRetailer(this.form);
                } catch (e) {
                    this.errors = errorHandler(e);
                    this.$wait.end(LOADER_NEW_RETAILER_SUBMISSION);
                    return;
                }

                this.$wait.end(LOADER_NEW_RETAILER_SUBMISSION);
                this.company = result.data.company;

                this.errors = {};
                this.step++;
            },
            async submit() {
                this.$wait.start(LOADER_NEW_RETAILER_SUBMISSION);

                let result = null;
                try {
                    result = await Profile.submitNewRetailer(this.form);
                    this.$wait.end(LOADER_NEW_RETAILER_SUBMISSION);
                } catch (e) {
                    this.errors = errorHandler(e);
                    this.$wait.end(LOADER_NEW_RETAILER_SUBMISSION);
                    return;
                }

                this.errors = {};
                logOutUser(true, ROUTE_CHANGE_RETAILER_SUCCESS);
            },
            nextStep() {
                if (this.step <= this.maxSteps) {
                    this.step++;
                }
            },
            previousStep() {
                if (this.step >= 0) {
                    this.step--;
                }
            },
        },
        watch: {
            step() {
                this.$vuetify.goTo(0);
            },
        },
        mounted() {
            this.step = 0;
            this.newRetailerName = '';
        },
    }
</script>
